<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query'
import { fetchInvoiceTemplateQueryOptions } from '@/features/invoices/queries'
import InvoiceSkeleton from '@/features/invoices/components/partials/InvoiceSkeleton.vue'
import InvoiceSkeletonInvoiceDetails from '@/features/invoices/components/partials/InvoiceSkeletonInvoiceDetails.vue'
import InvoiceSkeletonBillTotal from '@/features/invoices/components/partials/InvoiceSkeletonBillTotal.vue'
import InvoiceSkeletonCustomerDetails from '@/features/invoices/components/partials/InvoiceSkeletonCustomerDetails.vue'
import { computed, ref, useTemplateRef } from 'vue'
import InvoiceSkeletonCharges from '@/features/invoices/components/partials/InvoiceSkeletonCharges.vue'
import { ChargeType } from '@/api/billing/constants'
import PageContent from '@/components/page-content.vue'
import InvoiceSkeletonOwnerDetails from '@/features/invoices/components/partials/InvoiceSkeletonOwnerDetails.vue'
import ButtonPrimary from '@/components/button-primary.vue'
import { faFloppyDisk, faXmark } from '@fortawesome/pro-regular-svg-icons'
import ButtonSecondary from '@/components/button-secondary.vue'
import { watchOnce } from '@vueuse/core'
import InvoiceSkeletonSummary from '@/features/invoices/components/partials/InvoiceSkeletonSummary.vue'
import InvoiceSkeletonBankingDetails from '@/features/invoices/components/partials/InvoiceSkeletonBankingDetails.vue'
import InvoiceSkeletonMeterReadings from '@/features/invoices/components/partials/InvoiceSkeletonMeterReadings.vue'
import useLoader from '@/composables/loader'
import useNotifications from '@/composables/notifications'
import { useUpdateAssetInvoiceConfig } from '@/composables/billing/invoices'
import { useUpdatePoNumber } from '@/composables/assets/projects'
import { useSaveMeterConfiguration } from '@/composables/assets/assets'
import { useUpdateInvoiceTemplateCategories } from '@/features/invoices/mutations'
import ContentLoader from '@/components/content-loader.vue'
import HeroSplashError from '@/components/hero-splash-error.vue'
import { assetQueries } from '@/features/assets/queries'
import InvoiceSkeletonSummaryFullWidth
  from '@/features/invoices/components/partials/InvoiceSkeletonSummaryFullWidth.vue'
import { AssetType } from '@/features/assets/constants'
import InvoiceSkeletonWheelingCharges from './partials/wheeling/InvoiceSkeletonCharges.vue'
import { PSO, Season } from '@/features/tariffs/constants'

const props = defineProps<{ assetCode: string }>()
const chargesComponent = useTemplateRef<InstanceType<typeof InvoiceSkeletonCharges>>('charges')
const metersComponent = useTemplateRef<InstanceType<typeof InvoiceSkeletonMeterReadings>>('meters')
const { wrap } = useLoader()
const { addSuccess, addError } = useNotifications()
const { mutateAsync: updateInvoiceConfig } = useUpdateAssetInvoiceConfig(props.assetCode)
const { mutateAsync: updateProject } = useUpdatePoNumber(props.assetCode)
const { mutateAsync: updateMeterConfig } = useSaveMeterConfiguration(props.assetCode)
const { mutateAsync: updateChargeCategories } = useUpdateInvoiceTemplateCategories(props.assetCode)

const poNumber = ref<string | null>('')
const showSavings = ref(true)
const showMeters = ref(true)

const { isLoading: isLoadingTemplate, isError, error, data: template } = useQuery(fetchInvoiceTemplateQueryOptions(props.assetCode))
const { data: assetDetails } = useQuery(assetQueries.details(() => props.assetCode))

function persistChanges() {
  if (!template.value) {
    addError({
      title: 'Unable to update invoice template.',
      message: 'An unexpected internal state was encountered. Please contact the tech team to resolve this.',
    })
    return
  }

  wrap(
    async () => {
      const changedMeters = metersComponent.value?.changes() ?? []
      const changedCategories = chargesComponent.value?.changes()?.categories ?? []

      // Add in project update
      await Promise.all(
        [
          updateInvoiceConfig({
            id: template.value.billingConfigurationId,
            showMeterReadings: showMeters.value,
            showSavings: showSavings.value,
          }),
          updateProject({
            projectCode: template.value.projectCode,
            poNumber: poNumber.value
          })
        ].concat(
          changedMeters.length > 0
            ? [updateMeterConfig(changedMeters.map(x => ({ id: x.id, hide: x.values?.hide ?? false })))]
            : []
        ).concat(
          changedCategories.length > 0
            ? [updateChargeCategories(changedCategories.map(x => ({ ...x.values, id: x.id })))]
            : []
        )
      )
    }
  ).then(
    () => {
      addSuccess({ title: 'Invoice template updated successfully.' })

      metersComponent.value?.clearChanges()
      chargesComponent.value?.clearChanges()
    },
    () => {
      addError({ title: 'Unable to update invoice template', message: 'Please try again' })
    }
  )
}

// Update some values when the template loads initially.
watchOnce(
  template,
  newValue => {
    if (newValue === undefined)
      return

    poNumber.value = newValue.poNumber
    showSavings.value = newValue.showSavings
    showMeters.value = newValue.showMeterReadings
  }
)

const meterReadings = computed(
  () => (template.value?.meterReadings ?? []).map(
    x => ({
      ...x,
      forwardActiveStartReading: null,
      forwardActiveEndReading: null,
      reverseActiveStartReading: null,
      reverseActiveEndReading: null,
    })
  )
)
</script>

<template>
  <PageContent>
    <ContentLoader v-if="isLoadingTemplate" :loading="isLoadingTemplate" />
    <HeroSplashError v-else-if="isError" />
    <InvoiceSkeleton v-else-if="!isLoadingTemplate && template" title="Tax Invoice Template">
      <template #invoice-details>
        <InvoiceSkeletonInvoiceDetails :date-format="template.invoiceDateFormat" />
      </template>

      <template #bill-total>
        <InvoiceSkeletonBillTotal :amount="template.totalAmountIncludingTax"
                                  :due-day="template.dueDate.getDate()" />
      </template>

      <template #owner-details>
        <InvoiceSkeletonOwnerDetails :name="template.contractFundEntity.name"
                                     :address="template.contractFundEntity.address"
                                     :logo-url="template.contractFundEntity.logoUrl"
                                     :vat-number="template.contractFundEntity.vatNumber" />
      </template>

      <template #customer-details>
        <InvoiceSkeletonCustomerDetails :name="template.customer.name"
                                        :address="[
                                          template.customer.addressLineOne,
                                          template.customer.addressLineTwo,
                                          template.customer.suburb,
                                          template.customer.city,
                                          template.customer.region,
                                          template.customer.country,
                                        ]"
                                        :vat-number="template.customer.vatNumber"
                                        v-model:po-number="poNumber" />
      </template>
      <template #charges>
        <InvoiceSkeletonCharges ref="charges"
                                :categories="template.categories"
                                :capabilities="['add-charge']"
                                :asset-code="assetCode"
                                :total-excluding-tax="template.totalAmountExcludingTax"
                                :total-including-tax="template.totalAmountIncludingTax"
                                :tax="template.tax"
                                :tax-percentage="template.taxPercentage"
                                :editable-charge-types="[ChargeType.Recurring]"
                                :removable-charge-types="[ChargeType.Recurring]"
                                :summary-type="assetDetails?.type !== AssetType.Wheeling ? 'all' : 'category'"
        />
      </template>

      <template #meters v-if="assetDetails?.type !== AssetType.Wheeling">
        <InvoiceSkeletonMeterReadings ref="meters"
                                      :exported="template.isExportPower"
                                      v-model:show="showMeters"
                                      :exported-energy="template.totalEnergyExported"
                                      :grid-consumption="template.totalGridConsumption"
                                      :solar-consumption="template.totalEnergyConsumption"
                                      :meters="meterReadings" />
      </template>

      <template #summary v-if="assetDetails?.type !== AssetType.Wheeling">
        <InvoiceSkeletonSummary v-model:show="showSavings"
                                :savings-to-date="template.totalSavingsToDate"
                                :savings="template.invoiceSavings?.amount ?? 0"
                                :amount-exc-tax="template.totalAmountExcludingTax" />
      </template>

      <template #summary-full-width v-else>
        <InvoiceSkeletonSummaryFullWidth :amount-excluding-tax-in-cents="template.totalAmountExcludingTax * 100"
                                         :credit-amount-in-cents="template.totalCreditedInRands * 100"
                                         :tax-percentage="template.taxPercentage"
                                         :tax-amount-in-cents="template.tax * 100"
                                         :amount-due-in-cents="template.totalAmountDueInRands * 100" />
      </template>

      <template #banking-details>
        <InvoiceSkeletonBankingDetails :account-holder="template.contractFundEntity.accountName"
                                       :bank="template.contractFundEntity.bank"
                                       :account-number="template.contractFundEntity.accountNumber"
                                       :branch-code="template.contractFundEntity.branchCode" />
      </template>

      <template #footer>
        <ButtonPrimary :icon="faFloppyDisk" @click="persistChanges">Save</ButtonPrimary>
        <ButtonSecondary :icon="faXmark" @click="$router.push({ name: 'invoices.list' })">Cancel</ButtonSecondary>
      </template>
    </InvoiceSkeleton>
  </PageContent>
</template>
